import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Eye, EyeOff, RefreshCw, UserPlus, AlertCircle, GraduationCap, Users, Building2, Landmark, Briefcase } from 'lucide-react';

const signupSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name too long'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(8, 'Please confirm your password'),
  sector: z.enum(['public', 'private'], {
    required_error: 'Please select your sector',
  }),
  role: z.enum(['teacher', 'headteacher', 'siso', 'director', 'other_ges_staff'], {
    required_error: 'Please select your role',
  }),
  school: z.string().optional(),
  circuit_name: z.string().optional(),
  district_name: z.string().optional(),
  region_name: z.string().optional(),
  captcha_answer: z.number().min(1, 'Please solve the CAPTCHA'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
}).refine((data) => {
  // School is required for headteachers
  if (data.role === 'headteacher' && (!data.school || data.school.trim() === '')) {
    return false;
  }
  return true;
}, {
  message: "School name is required for headteachers",
  path: ["school"],
}).refine((data) => {
  // Circuit name is required for SISO
  if (data.role === 'siso' && (!data.circuit_name || data.circuit_name.trim() === '')) {
    return false;
  }
  return true;
}, {
  message: "Circuit name is required for SISO",
  path: ["circuit_name"],
}).refine((data) => {
  // District name is required for SISO and Director
  if ((data.role === 'siso' || data.role === 'director') && (!data.district_name || data.district_name.trim() === '')) {
    return false;
  }
  return true;
}, {
  message: "District name is required",
  path: ["district_name"],
}).refine((data) => {
  // Region name is required for Director
  if (data.role === 'director' && (!data.region_name || data.region_name.trim() === '')) {
    return false;
  }
  return true;
}, {
  message: "Region name is required for Director",
  path: ["region_name"],
});

export const SignupForm = ({ onSuccess, onSwitchToLogin }) => {
  const { signup, getCaptcha } = useAuth();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(signupSchema),
  });

  const watchedRole = watch('role');
  const watchedSector = watch('sector');

  useEffect(() => {
    loadCaptcha();
  }, []);

  const loadCaptcha = async (retryCount = 0) => {
    console.log('📋 Loading CAPTCHA... (attempt:', retryCount + 1, ')');
    const result = await getCaptcha();
    console.log('CAPTCHA result:', result);
    if (result.success) {
      setCaptcha(result.data);
      setValue('captcha_answer', '');
      setError(''); // Clear any previous errors
      console.log('✅ CAPTCHA loaded successfully:', result.data);
    } else {
      console.error('❌ Failed to load CAPTCHA:', result.error);
      // Retry up to 2 times with a delay
      if (retryCount < 2) {
        console.log('🔄 Retrying CAPTCHA load in 1 second...');
        setTimeout(() => loadCaptcha(retryCount + 1), 1000);
      } else {
        setError(`Failed to load security check: ${result.error}. Please refresh the page.`);
      }
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    // Ensure we have a valid CAPTCHA session
    if (!captcha || !captcha.session_id) {
      setError('CAPTCHA session expired. Please refresh.');
      await loadCaptcha();
      setLoading(false);
      return;
    }

    const signupData = {
      name: data.name,
      email: data.email,
      password: data.password,
      role: data.role,
      sector: data.sector || 'public',
      school: data.role === 'headteacher' ? data.school : undefined,
      circuit_name: data.role === 'siso' ? data.circuit_name : undefined,
      district_name: (data.role === 'siso' || data.role === 'director') ? data.district_name : undefined,
      region_name: data.role === 'director' ? data.region_name : undefined,
      captcha_session_id: captcha.session_id,
      captcha_answer: parseInt(data.captcha_answer),
    };

    const result = await signup(signupData);
    
    if (result.success) {
      // Show success toast
      toast({
        title: "Account Created Successfully! 🎉",
        description: `Welcome ${data.name}! Please log in with your credentials.`,
        variant: "default",
      });
      
      // Switch to login form after brief delay
      setTimeout(() => {
        onSwitchToLogin?.();
      }, 1500);
    } else {
      setError(result.error);
      // Show error toast
      toast({
        title: "Signup Failed",
        description: result.error,
        variant: "destructive",
      });
      // Reload captcha on error
      loadCaptcha();
    }
    
    setLoading(false);
  };

  return (
    <Card className="w-full max-w-md mx-auto border-0 shadow-2xl bg-white/95 backdrop-blur-sm max-h-[90vh] flex flex-col">
      <CardHeader className="space-y-2 text-center flex-shrink-0 py-4">
        <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-red-500 rounded-full flex items-center justify-center mx-auto">
          <UserPlus className="h-6 w-6 text-white" />
        </div>
        <CardTitle className="text-xl font-bold bg-gradient-to-r from-pink-600 to-red-600 bg-clip-text text-transparent">
          Join EduLegalHub
        </CardTitle>
        <CardDescription className="text-sm text-gray-600">
          Create your account to get started
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 overflow-y-auto flex-1 px-6">
        {error && (
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          {/* Full Name Field */}
          <div className="space-y-1">
            <Label htmlFor="name" className="text-sm font-medium text-gray-700">
              Full Name
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your full name"
              className="h-10 border-gray-300 focus:border-pink-500 focus:ring-pink-500"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-xs text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* Email Field */}
          <div className="space-y-1">
            <Label htmlFor="email" className="text-sm font-medium text-gray-700">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              className="h-10 border-gray-300 focus:border-pink-500 focus:ring-pink-500"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-xs text-red-600">{errors.email.message}</p>
            )}
          </div>

          {/* Sector Selection */}
          <div className="space-y-1">
            <Label htmlFor="sector" className="text-sm font-medium text-gray-700">
              Sector
            </Label>
            <Select onValueChange={(value) => {
              setValue('sector', value);
              setValue('role', undefined); // Reset role when sector changes
            }}>
              <SelectTrigger className="h-10 border-gray-300 focus:border-pink-500 focus:ring-pink-500">
                <SelectValue placeholder="Select your sector" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">
                  <div className="flex items-center space-x-2">
                    <Landmark className="h-4 w-4 text-blue-600" />
                    <span>Public Sector (GES)</span>
                  </div>
                </SelectItem>
                <SelectItem value="private">
                  <div className="flex items-center space-x-2">
                    <Briefcase className="h-4 w-4 text-orange-600" />
                    <span>Private Sector</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            {errors.sector && (
              <p className="text-xs text-red-600">{errors.sector.message}</p>
            )}
          </div>

          {/* Role Selection - Conditional based on sector */}
          {watchedSector && (
          <div className="space-y-1">
            <Label htmlFor="role" className="text-sm font-medium text-gray-700">
              Your Role
            </Label>
            <Select key={watchedSector} onValueChange={(value) => setValue('role', value)}>
              <SelectTrigger className="h-10 border-gray-300 focus:border-pink-500 focus:ring-pink-500">
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent>
                {watchedSector === 'public' ? (
                  <>
                    <SelectItem value="teacher">
                      <div className="flex items-center space-x-2">
                        <GraduationCap className="h-4 w-4 text-blue-600" />
                        <span>Teacher</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="headteacher">
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-purple-600" />
                        <span>Headteacher</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="siso">
                      <div className="flex items-center space-x-2">
                        <Building2 className="h-4 w-4 text-green-600" />
                        <span>SISO</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="director">
                      <div className="flex items-center space-x-2">
                        <Landmark className="h-4 w-4 text-orange-600" />
                        <span>District Director</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="other_ges_staff">
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-teal-600" />
                        <span>Other GES Staff</span>
                      </div>
                    </SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="teacher">
                      <div className="flex items-center space-x-2">
                        <GraduationCap className="h-4 w-4 text-blue-600" />
                        <span>Teacher</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="headteacher">
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-purple-600" />
                        <span>Headteacher</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="siso">
                      <div className="flex items-center space-x-2">
                        <Building2 className="h-4 w-4 text-green-600" />
                        <span>Proprietor(ress)</span>
                      </div>
                    </SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
            {errors.role && (
              <p className="text-xs text-red-600">{errors.role.message}</p>
            )}
            {watchedRole && (
              <div className="text-xs text-gray-500 bg-gray-50 p-1.5 rounded text-center">
                {watchedRole === 'teacher' && 'Access lesson management & upload notes'}
                {watchedRole === 'headteacher' && 'Manage teachers & oversee activities'}
                {watchedRole === 'siso' && watchedSector === 'public' && 'School Improvement Support Officer - Circuit oversight'}
                {watchedRole === 'siso' && watchedSector === 'private' && 'Proprietor(ress) - School ownership & management'}
                {watchedRole === 'director' && 'District Director - Full district oversight & management'}
                {watchedRole === 'other_ges_staff' && 'Other GES Staff - Access promotion exams preparation & more'}
              </div>
            )}
          </div>
          )}

          {/* School Field - Only for Headteachers */}
          {watchedRole === 'headteacher' && (
            <div className="space-y-1">
              <Label htmlFor="school" className="text-sm font-medium text-gray-700">
                School Name
              </Label>
              <Input
                id="school"
                type="text"
                placeholder="Enter your school name"
                className="h-10 border-gray-300 focus:border-pink-500 focus:ring-pink-500"
                {...register('school')}
              />
              {errors.school && (
                <p className="text-xs text-red-600">{errors.school.message}</p>
              )}
              <p className="text-xs text-gray-500">
                This helps organize your school&apos;s data separately from other schools
              </p>
            </div>
          )}

          {/* SISO Fields - Circuit and District */}
          {watchedRole === 'siso' && (
            <>
              <div className="space-y-1">
                <Label htmlFor="circuit_name" className="text-sm font-medium text-gray-700">
                  Circuit Name
                </Label>
                <Input
                  id="circuit_name"
                  type="text"
                  placeholder="Enter your circuit name"
                  className="h-10 border-gray-300 focus:border-pink-500 focus:ring-pink-500"
                  {...register('circuit_name')}
                />
                {errors.circuit_name && (
                  <p className="text-xs text-red-600">{errors.circuit_name.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="district_name" className="text-sm font-medium text-gray-700">
                  District Name
                </Label>
                <Input
                  id="district_name"
                  type="text"
                  placeholder="Enter your district name"
                  className="h-10 border-gray-300 focus:border-pink-500 focus:ring-pink-500"
                  {...register('district_name')}
                />
                {errors.district_name && (
                  <p className="text-xs text-red-600">{errors.district_name.message}</p>
                )}
              </div>
            </>
          )}

          {/* Director Fields - District and Region */}
          {watchedRole === 'director' && (
            <>
              <div className="space-y-1">
                <Label htmlFor="district_name" className="text-sm font-medium text-gray-700">
                  District Name
                </Label>
                <Input
                  id="district_name"
                  type="text"
                  placeholder="Enter your district name"
                  className="h-10 border-gray-300 focus:border-pink-500 focus:ring-pink-500"
                  {...register('district_name')}
                />
                {errors.district_name && (
                  <p className="text-xs text-red-600">{errors.district_name.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="region_name" className="text-sm font-medium text-gray-700">
                  Region Name
                </Label>
                <Input
                  id="region_name"
                  type="text"
                  placeholder="Enter your region name"
                  className="h-10 border-gray-300 focus:border-pink-500 focus:ring-pink-500"
                  {...register('region_name')}
                />
                {errors.region_name && (
                  <p className="text-xs text-red-600">{errors.region_name.message}</p>
                )}
              </div>
            </>
          )}

          {/* Password Field */}
          <div className="space-y-1">
            <Label htmlFor="password" className="text-sm font-medium text-gray-700">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Create a password"
                className="h-10 pr-10 border-gray-300 focus:border-pink-500 focus:ring-pink-500"
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-red-600">{errors.password.message}</p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="space-y-1">
            <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
              Confirm Password
            </Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                className="h-10 pr-10 border-gray-300 focus:border-pink-500 focus:ring-pink-500"
                {...register('confirmPassword')}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-xs text-red-600">{errors.confirmPassword.message}</p>
            )}
          </div>

          {/* CAPTCHA Field */}
          <div className="space-y-1">
            <Label htmlFor="captcha" className="text-sm font-medium text-gray-700">
              Security Check
            </Label>
            <div className="flex items-center space-x-2">
              <div className="flex-1">
                <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded border">
                  <span className="text-sm font-mono font-bold text-gray-800">
                    {captcha?.question || 'Loading...'}
                  </span>
                  <button
                    type="button"
                    onClick={loadCaptcha}
                    className="p-1 hover:bg-gray-200 rounded"
                    disabled={!captcha}
                  >
                    <RefreshCw className="h-3 w-3 text-gray-600" />
                  </button>
                </div>
              </div>
              <div className="w-20">
                <Input
                  id="captcha"
                  type="number"
                  placeholder="Answer"
                  className="h-10 text-center text-sm font-mono border-gray-300 focus:border-pink-500 focus:ring-pink-500"
                  {...register('captcha_answer', { valueAsNumber: true })}
                />
              </div>
            </div>
            {errors.captcha_answer && (
              <p className="text-xs text-red-600">{errors.captcha_answer.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting || loading || !captcha}
            className="w-full h-10 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 mt-4"
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Creating Account...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <UserPlus className="h-4 w-4" />
                <span>Create Account</span>
              </div>
            )}
          </Button>
        </form>

        {/* Switch to Login */}
        <div className="text-center pt-3 border-t border-gray-200 flex-shrink-0">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="text-pink-600 hover:text-pink-700 font-semibold transition-colors"
            >
              Sign in here
            </button>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};